import React, { useState } from 'react';
import { useStore } from '../store';
import { AnalysisResults } from '../types';
import { BarChart, PieChart, LineChart, Play, AlertCircle, ChevronDown, ChevronUp, Download, Sparkles, TrendingUp, Users, Target } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ColumnDetector } from '../utils/columnDetection';
import { ScaleDetector, ScaleInfo } from '../utils/scaleDetection';
import { DataValidator } from '../utils/dataValidation';

export function Dashboard() {
  const { 
    fileData,
    workshopColumn,
    axes,
    scaleMapping,
    setAnalysisResults,
    setCurrentPage,
    analysisResults,
    canProceedToAnalysis,
    setAxes
  } = useStore();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedWorkshop, setExpandedWorkshop] = useState<string | null>(null);
  const [exportingWorkshop, setExportingWorkshop] = useState<string | null>(null);

  const handleExportWorkshop = async (workshopId: string) => {
    try {
      setExportingWorkshop(workshopId);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('يجب تسجيل الدخول أولاً لتصدير النتائج');
      }

      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysisResults,
          axes,
          workshopColumn,
          format: 'csv',
          workshopId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `فشل تصدير الملف: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'فشل تصدير الملف');
      }

      // Convert base64 to Uint8Array
      const binaryString = atob(result.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob with UTF-8 encoding
      const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Export error:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تصدير النتائج');
    } finally {
      setExportingWorkshop(null);
    }
  };

  const startAnalysis = async () => {
    if (!canProceedToAnalysis()) return;

    try {
      setIsAnalyzing(true);
      setError(null);
      
      // تشخيص أولي للبيانات
      console.log('🚀 بدء التحليل - تشخيص أولي:');
      console.log('📊 عدد الصفوف في fileData:', fileData?.length);
      console.log('🏭 عمود الورشة المحدد:', workshopColumn);
      console.log('📋 عينة من البيانات الأولى:', fileData?.[0]);
      
      // التحقق الشامل من البيانات قبل التحليل
      const questionColumns = Object.values(axes).flat();
      const validation = DataValidator.validateAnalysisData(fileData || [], workshopColumn || '', questionColumns);
      
      console.log('🛡️ نتائج التحقق من البيانات:', validation);
      
      if (!validation.isValid) {
        setError(`أخطاء في البيانات: ${validation.errors.join(', ')}`);
        return;
      }
      
      if (validation.warnings.length > 0) {
        console.warn('⚠️ تحذيرات:', validation.warnings);
      }
      
      const scaleInfo = validation.scaleInfo;
      console.log(`📏 المقياس المكتشف: ${scaleInfo.type} (ثقة: ${(scaleInfo.confidence * 100).toFixed(1)}%)`);
      
      // تحديث دالة تحديد مستوى الأداء لتستخدم المقياس المكتشف
      const determinePerformanceLevelSafe = (average: number) => 
        ScaleDetector.determinePerformanceLevel(average, scaleInfo);
      
      if (!fileData || fileData.length === 0) {
        setError('لا توجد بيانات للتحليل');
        return;
      }
      
      if (!workshopColumn) {
        setError('لم يتم تحديد عمود الورشة');
        return;
      }

      // الحصول على أسماء الأعمدة من البيانات
      const headers = fileData && fileData.length > 0 ? Object.keys(fileData[0]) : [];
      
      // التحقق من وجود عمود الورشة في البيانات
      if (!headers.includes(workshopColumn)) {
        console.error(`❌ عمود الورشة "${workshopColumn}" غير موجود في البيانات!`);
        console.log('📋 الأعمدة المتاحة:', headers);
        setError(`عمود الورشة "${workshopColumn}" غير موجود في البيانات. الأعمدة المتاحة: ${headers.join(', ')}`);
        return;
      }
      
      // فحص القيم في عمود الورشة
      const workshopValues = fileData.map(row => row[workshopColumn]).filter(v => v != null && v !== '');
      console.log('🔍 القيم الفريدة في عمود الورشة:', [...new Set(workshopValues)]);
      console.log('📊 عدد القيم غير الفارغة:', workshopValues.length);
      console.log('📊 إجمالي الصفوف:', fileData.length);
      
      if (workshopValues.length === 0) {
        setError(`عمود الورشة "${workshopColumn}" لا يحتوي على أي قيم صالحة. جميع القيم فارغة أو null.`);
        return;
      }
      
      const detectionResult = ColumnDetector.detectColumns(headers, fileData || []);
      console.log('🔍 نتائج اكتشاف الأعمدة:', detectionResult);

      // فلترة الأسئلة من أعمدة ID والمعرفات
      const actualQuestionColumns = detectionResult.questionColumns.filter((header: string) => {
        // إزالة أعمدة ID المكتشفة من قائمة الأسئلة
        const isIdColumn = detectionResult.identifierColumns.includes(header);
        if (isIdColumn) {
          console.log(`🚫 استبعاد العمود "${header}" من الأسئلة (تم تصنيفه كمعرف)`);
          return false;
        }
        return true;
      });

      console.log(`📋 الأسئلة الفعلية: ${actualQuestionColumns.length} من ${headers.length} عمود`);
      console.log('📝 أعمدة الأسئلة:', actualQuestionColumns);
      console.log('🆔 أعمدة المعرفات المستبعدة:', detectionResult.identifierColumns);

      // تجميع الأسئلة في محاور مع تمرير البيانات للتحليل
      const initialAxes = ColumnDetector.groupQuestionsIntoAxes(actualQuestionColumns, fileData || []);
      
      // تحديث المحاور في الـ store (مهم!)
      setAxes(initialAxes);

      console.log('📊 المحاور المحدثة:', Object.keys(initialAxes));
      console.log('🔍 تجميع البيانات حسب الورش...');

      // Group data by workshop
      console.log('🔍 عمود الورشة المحدد:', workshopColumn);
      console.log('🔍 عينة من البيانات:', fileData?.slice(0, 3));
      console.log('🔍 أسماء الأعمدة المتاحة:', fileData && fileData.length > 0 ? Object.keys(fileData[0]) : []);
      
      const workshopGroups = fileData?.reduce((acc, row, index) => {
        let workshop = row[workshopColumn as string];
        
        // تنظيف القيمة من المسافات الزائدة
        if (typeof workshop === 'string') {
          workshop = workshop.trim();
        }
        
        console.log(`🔍 الصف ${index + 1}: عمود الورشة "${workshopColumn}" = "${workshop}" (نوع: ${typeof workshop})`);
        
        if (workshop && workshop !== '' && workshop !== null && workshop !== undefined) {
          // تحويل القيمة إلى نص للتأكد من التوافق
          const workshopKey = String(workshop);
          if (!acc[workshopKey]) acc[workshopKey] = [];
          acc[workshopKey].push(row);
          console.log(`✅ تم إضافة الصف ${index + 1} للورشة "${workshopKey}"`);
        } else {
          console.warn(`⚠️ الصف ${index + 1} بدون ورشة محددة:`, { 
            workshopColumn, 
            workshopValue: workshop, 
            workshopType: typeof workshop,
            allRowKeys: Object.keys(row),
            rowSample: Object.fromEntries(Object.entries(row).slice(0, 3))
          });
        }
        return acc;
      }, {} as Record<string, any[]>) || {};

      console.log('🏭 الورش المكتشفة:', Object.keys(workshopGroups));
      console.log('📈 أعداد البيانات لكل ورشة:', Object.entries(workshopGroups).map(([name, data]) => `${name}: ${data.length} صف`));
      
      // تحقق إضافي من البيانات
      if (Object.keys(workshopGroups).length === 0) {
        console.error('❌ لم يتم العثور على أي ورش! المشكلة قد تكون:');
        console.error('1. اسم عمود الورشة غير صحيح');
        console.error('2. البيانات لا تحتوي على قيم في عمود الورشة');
        console.error('3. تنسيق البيانات غير متوافق');
        
        // عرض عينة من البيانات للتشخيص
        if (fileData && fileData.length > 0) {
          console.log('🔍 عينة موسعة من البيانات للتشخيص:');
          fileData.slice(0, 5).forEach((row, index) => {
            console.log(`الصف ${index + 1}:`, row);
          });
        }
      }
      
      // التحقق من أن كل ورشة لديها بيانات
      Object.entries(workshopGroups).forEach(([workshop, rows]) => {
        if (rows.length === 0) {
          console.warn(`⚠️ الورشة "${workshop}" لا تحتوي على بيانات!`);
        } else {
          console.log(`✅ الورشة "${workshop}" تحتوي على ${rows.length} استجابة`);
        }
      });

      // Calculate results for each axis
      const results = {
        overall: { average: 0, performance_level: '', total_responses: 0 },
        by_axis: {} as Record<string, any>,
        by_workshop: {} as Record<string, any>
      };

      // التحقق من وجود ورش قبل المعالجة
      if (Object.keys(workshopGroups).length === 0) {
        console.error('❌ لا يمكن المتابعة: لم يتم العثور على أي ورش عمل');
        
        // محاولة إصلاح المشكلة تلقائياً
        console.log('🔧 محاولة إصلاح المشكلة تلقائياً...');
        
        // البحث عن أعمدة محتملة للورش
        const potentialWorkshopColumns = headers.filter(header => {
          const lowerHeader = header.toLowerCase();
          return lowerHeader.includes('ورشة') || 
                 lowerHeader.includes('workshop') || 
                 lowerHeader.includes('دورة') || 
                 lowerHeader.includes('course') ||
                 lowerHeader.includes('برنامج') ||
                 lowerHeader.includes('program') ||
                 lowerHeader.includes('اسم') ||
                 lowerHeader.includes('name');
        });
        
        console.log('🔍 الأعمدة المحتملة للورش:', potentialWorkshopColumns);
        
        if (potentialWorkshopColumns.length > 0) {
          const suggestedColumn = potentialWorkshopColumns[0];
          console.log(`💡 اقتراح استخدام العمود: "${suggestedColumn}"`);
          
          setError(`لم يتم العثور على ورش عمل في العمود المحدد "${workshopColumn}". هل تريد استخدام العمود "${suggestedColumn}" بدلاً من ذلك؟ أو يرجى العودة لاختيار عمود الورشة الصحيح.`);
        } else {
          setError(`لم يتم العثور على ورش عمل في العمود المحدد "${workshopColumn}". يرجى العودة لاختيار عمود الورشة الصحيح. الأعمدة المتاحة: ${headers.join(', ')}`);
        }
        return;
      }

      // Process each workshop
      console.log('🔄 بدء معالجة الورش...');
      for (const [workshop, rows] of Object.entries(workshopGroups)) {
        console.log(`🏭 معالجة الورشة "${workshop}" مع ${rows.length} صف`);
        
        const workshopResults = {
          overall_average: 0,
          by_axis: {} as Record<string, any>,
          performance_level: '',
          response_count: rows.length // Set the response count immediately when processing the workshop
        };
        
        console.log(`📊 تم تعيين response_count = ${rows.length} للورشة "${workshop}"`);
        
        // تحقق فوري من القيمة
        if (workshopResults.response_count === 0) {
          console.error(`❌ خطأ: response_count = 0 للورشة "${workshop}" رغم وجود ${rows.length} صف!`);
        }

        // Process each axis
        for (const [axisName, questions] of Object.entries(initialAxes)) {
          const axisValues: number[] = [];
          const questionAverages: Record<string, number> = {};

          // Process each question
          for (const question of questions) {
            const values = rows
              .map(row => {
                const response = row[question];
                let normalizedValue: number | null = null;
                
                if (typeof response === 'number') {
                  // استخدام القيم كما هي بدون تطبيع مدمر
                  normalizedValue = response;
                } else if (typeof response === 'string') {
                  const scaleValue = scaleMapping[response.trim()];
                  if (scaleValue !== undefined) {
                    normalizedValue = scaleValue;
                  } else {
                    const numericValue = Number(response);
                    if (!isNaN(numericValue)) {
                      normalizedValue = numericValue;
                    }
                  }
                }
                
                return normalizedValue;
              })
              .filter((v): v is number => typeof v === 'number' && !isNaN(v) && v > 0);

            if (values.length > 0) {
              const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
              questionAverages[question] = avg;
              axisValues.push(avg);
            }
          }

          if (axisValues.length > 0) {
            const axisAverage = axisValues.reduce((sum, val) => sum + val, 0) / axisValues.length;
            workshopResults.by_axis[axisName] = {
              average: axisAverage,
              question_averages: questionAverages,
              performance_level: determinePerformanceLevelSafe(axisAverage)
            };
          }
        }

        // Calculate workshop overall average
        const axisAverages = Object.values(workshopResults.by_axis).map(a => a.average);
        if (axisAverages.length > 0) {
          workshopResults.overall_average = axisAverages.reduce((sum, val) => sum + val, 0) / axisAverages.length;
          workshopResults.performance_level = determinePerformanceLevelSafe(workshopResults.overall_average);
        }

        results.by_workshop[workshop] = workshopResults;
        
        // تحقق نهائي من القيمة قبل الحفظ
        console.log(`✅ تم حفظ الورشة "${workshop}" مع response_count = ${workshopResults.response_count}`);
        if (workshopResults.response_count === 0) {
          console.error(`❌ تحذير: تم حفظ الورشة "${workshop}" مع response_count = 0!`);
        }
      }

      // Calculate overall axis averages
      for (const [axisName, questions] of Object.entries(initialAxes)) {
        const axisData = {
          average: 0,
          question_averages: {} as Record<string, number>,
          performance_level: ''
        };

        // Calculate question averages across all workshops
        for (const question of questions) {
          const values = fileData
            ?.map(row => {
              const response = row[question];
              if (typeof response === 'number') return response;
              if (typeof response === 'string') {
                return scaleMapping[response.trim()] || Number(response);
              }
              return null;
            })
            .filter((v): v is number => typeof v === 'number' && !isNaN(v)) || [];

          if (values.length > 0) {
            axisData.question_averages[question] = values.reduce((sum, val) => sum + val, 0) / values.length;
          }
        }

        // Calculate axis average
        const questionAverages = Object.values(axisData.question_averages);
        if (questionAverages.length > 0) {
          axisData.average = questionAverages.reduce((sum, val) => sum + val, 0) / questionAverages.length;
          axisData.performance_level = determinePerformanceLevelSafe(axisData.average);
        }

        results.by_axis[axisName] = axisData;
      }

      // Calculate overall average
      const axisAverages = Object.values(results.by_axis).map(a => a.average);
      if (axisAverages.length > 0) {
        results.overall.average = axisAverages.reduce((sum, val) => sum + val, 0) / axisAverages.length;
        results.overall.performance_level = determinePerformanceLevelSafe(results.overall.average);
      }

      // Calculate total responses
      results.overall.total_responses = fileData?.length || 0;

      // Add response count to each workshop (verification step)
      for (const [workshop, rows] of Object.entries(workshopGroups)) {
        if (results.by_workshop[workshop]) {
          results.by_workshop[workshop].response_count = rows.length;
          console.log(`✅ تم تعيين عدد الاستجابات للورشة "${workshop}": ${rows.length}`);
        }
      }

      // Final verification of response counts before updating store
      console.log('🔍 التحقق النهائي من أعداد الاستجابات:');
      Object.entries(results.by_workshop).forEach(([workshop, data]) => {
        console.log(`📊 الورشة "${workshop}": ${data.response_count} استجابة`);
        if (data.response_count === undefined || data.response_count === null) {
          console.error(`❌ خطأ: عدد الاستجابات غير محدد للورشة "${workshop}"`);
        }
      });

      // تحقق أخير قبل حفظ النتائج
      console.log('💾 النتائج النهائية قبل الحفظ:');
      console.log('🏭 عدد الورش:', Object.keys(results.by_workshop).length);
      Object.entries(results.by_workshop).forEach(([workshop, data]) => {
        console.log(`📊 الورشة "${workshop}": response_count = ${data.response_count}, overall_average = ${data.overall_average}`);
      });

      // التحقق النهائي من سلامة النتائج
      const consistencyCheck = DataValidator.compareResults(
        fileData || [],
        results,
        questionColumns,
        scaleInfo
      );
      
      if (!consistencyCheck.isConsistent) {
        console.error('🚨 تحذير: تم اكتشاف مشاكل في النتائج:', consistencyCheck.issues);
        setError(`مشاكل في النتائج: ${consistencyCheck.issues.join(', ')}`);
        return;
      }
      
      // Update store with results
      setAnalysisResults(results);
      
      // تحقق بعد الحفظ
      console.log('✅ تم حفظ النتائج في Store بعد التحقق من سلامتها');
      
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تحليل البيانات');
    } finally {
      setIsAnalyzing(false);
    }
  };



  const getPerformanceLevelColor = (level: string): string => {
    switch (level) {
      case 'ممتاز': return '#4CAF50';
      case 'جيد جدا': return '#8BC34A';
      case 'جيد': return '#FFC107';
      case 'مقبول': return '#FF9800';
      case 'ضعيف': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  if (!analysisResults) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header with Gradient Text */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 gradient-text">
            تحليل البيانات 📊
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            اكتشف رؤى عميقة من بيانات ورش العمل باستخدام تقنيات التحليل المتقدمة
          </p>
        </div>
        
        {/* Main Analysis Card */}
        <div className="max-w-4xl mx-auto">
          <div className="modern-card p-8">
            <div className="text-center">
              {/* Icon with Animation */}
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-6 animate-pulse-glow">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-gray-800">
                {canProceedToAnalysis() ? 'جاهز للتحليل' : 'إعداد التحليل'}
              </h3>
              
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                {canProceedToAnalysis() 
                  ? 'تم تجهيز البيانات للتحليل. انقر على الزر أدناه لبدء عملية التحليل المتقدم.'
                  : 'يرجى إكمال الخطوات السابقة قبل بدء التحليل.'}
              </p>
              
              {/* Analysis Button */}
              <button
                onClick={startAnalysis}
                disabled={!canProceedToAnalysis() || isAnalyzing}
                className={`
                  btn-primary text-lg px-10 py-4 rounded-2xl transition-all duration-300
                  flex items-center gap-3 mx-auto shadow-xl
                  ${!canProceedToAnalysis() || isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                `}
              >
                {isAnalyzing ? (
                  <>
                    <div className="modern-spinner"></div>
                    <span>جاري التحليل...</span>
                  </>
                ) : (
                  <>
                    <Play className="w-6 h-6" />
                    <span>بدء التحليل المتقدم</span>
                  </>
                )}
              </button>

              {/* Error Display */}
              {error && (
                <div className="mt-8 notification-error">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                      {error.includes('لم يتم العثور على ورش عمل') && (
                        <button
                          onClick={() => setCurrentPage('columns')}
                          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
                        >
                          العودة لاختيار الأعمدة
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Preview */}
        <div className="max-w-6xl mx-auto mt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="modern-card p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-green-400 to-blue-500 mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">تحليل الأداء</h4>
              <p className="text-gray-600 text-sm">تحليل شامل لمستويات الأداء عبر جميع الورش</p>
            </div>
            
            <div className="modern-card p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">إحصائيات المشاركين</h4>
              <p className="text-gray-600 text-sm">معلومات مفصلة عن أعداد ومشاركة الحضور</p>
            </div>
            
            <div className="modern-card p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-orange-400 to-red-500 mb-4">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">تقارير تفصيلية</h4>
              <p className="text-gray-600 text-sm">تقارير قابلة للتصدير مع رؤى قابلة للتنفيذ</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Display with Modern Design
  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold mb-4 gradient-text">
          نتائج التحليل 📈
        </h2>
        <p className="text-lg text-gray-600">
          تحليل شامل لبيانات ورش العمل مع رؤى متقدمة
        </p>
      </div>

      {/* Overall Statistics Cards */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total Responses */}
          <div className="modern-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">إجمالي الاستجابات</p>
                <p className="text-3xl font-bold text-gray-900">{analysisResults.overall.total_responses}</p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-r from-blue-400 to-blue-600">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Overall Average */}
          <div className="modern-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">المتوسط العام</p>
                <p className="text-3xl font-bold text-gray-900">{analysisResults.overall.average.toFixed(2)}</p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-r from-green-400 to-green-600">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Performance Level */}
          <div className="modern-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">مستوى الأداء</p>
                <p className="text-xl font-bold text-gray-900">{analysisResults.overall.performance_level}</p>
              </div>
              <div className={`p-3 rounded-full ${getPerformanceLevelGradient(analysisResults.overall.performance_level)}`}>
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Number of Workshops */}
          <div className="modern-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">عدد الورش</p>
                <p className="text-3xl font-bold text-gray-900">{Object.keys(analysisResults.by_workshop).length}</p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-r from-purple-400 to-purple-600">
                <BarChart className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Diagnostic Panel */}
        <div className="modern-card p-6 mb-8">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            🔍 تشخيص سريع
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">عدد الورش: <span className="font-bold text-blue-600">{Object.keys(analysisResults.by_workshop).length}</span></p>
              <p className="text-sm font-medium text-gray-600 mb-4">إجمالي البيانات:</p>
              <p className="text-sm text-gray-500 mb-4">عمود الورشة: <span className="font-medium text-gray-700">{workshopColumn}</span></p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">أعداد الاستجابات لكل ورشة:</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {Object.entries(analysisResults.by_workshop).map(([workshop, data]) => (
                  <p key={workshop} className="text-xs text-gray-600">
                    <span className="font-medium">{workshop}:</span> 
                    <span className="text-blue-600 font-bold"> {data.response_count} استجابة</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => console.log('🔍 تشخيص مفصل:', analysisResults)}
              className="btn-secondary text-sm px-4 py-2 rounded-lg"
            >
              طباعة تشخيص مفصل في Console
            </button>
            <button
              onClick={handleFixResponseCount}
              className="btn-primary text-sm px-4 py-2 rounded-lg"
            >
              إصلاح عدد الاستجابات
            </button>
            <button
              onClick={handleClearAndRestart}
              className="bg-gradient-to-r from-red-500 to-pink-600 text-white text-sm px-4 py-2 rounded-lg hover:from-red-600 hover:to-pink-700 transition-all"
              title="⚠️ تحذير: سيمسح جميع النتائج الحالية!"
            >
              ⚠️ مسح كل شيء وبدء من جديد
            </button>
          </div>
        </div>

        {/* Workshops Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(analysisResults.by_workshop).map(([workshop, data]) => (
            <div key={workshop} className="modern-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex-1">{workshop}</h3>
                <button
                  onClick={() => setExpandedWorkshop(expandedWorkshop === workshop ? null : workshop)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {expandedWorkshop === workshop ? 
                    <ChevronUp className="w-5 h-5 text-gray-500" /> : 
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  }
                </button>
              </div>

              {/* Workshop Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                  <p className="text-sm text-gray-600">المتوسط</p>
                  <p className="text-2xl font-bold text-blue-600">{data.overall_average.toFixed(2)}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                  <p className="text-sm text-gray-600">الاستجابات</p>
                  <p className="text-2xl font-bold text-green-600">{data.response_count}</p>
                </div>
              </div>

              {/* Performance Badge */}
              <div className="flex items-center justify-center mb-4">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getPerformanceLevelClass(data.performance_level)}`}>
                  {data.performance_level}
                </span>
              </div>

              {/* Export Button */}
              <button
                onClick={() => handleExportWorkshop(workshop)}
                disabled={exportingWorkshop === workshop}
                className="w-full btn-primary py-2 rounded-lg flex items-center justify-center gap-2"
              >
                {exportingWorkshop === workshop ? (
                  <>
                    <div className="modern-spinner"></div>
                    <span>جاري التصدير...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>تصدير النتائج</span>
                  </>
                )}
              </button>

              {/* Expanded Details */}
              {expandedWorkshop === workshop && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold mb-4 text-gray-800">تفاصيل المحاور:</h4>
                  <div className="space-y-3">
                    {Object.entries(data.by_axis).map(([axis, axisData]) => (
                      <div key={axis} className="p-3 rounded-lg bg-gray-50">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">{axis}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">{axisData.average.toFixed(2)}</span>
                            <span className={`px-2 py-1 rounded text-xs ${getPerformanceLevelClass(axisData.performance_level)}`}>
                              {axisData.performance_level}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Export All Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => setCurrentPage('export')}
            className="btn-primary text-lg px-8 py-4 rounded-2xl shadow-xl hover:scale-105 transition-transform"
          >
            <Download className="w-5 h-5 mr-2" />
            تصدير جميع النتائج
          </button>
        </div>
      </div>
    </div>
  );

  // Helper function to fix response count
  function handleFixResponseCount() {
    if (!analysisResults || !fileData) return;
    
    console.log('🔧 إصلاح عدد الاستجابات...');
    
    // Recalculate response counts
    const workshopGroups = fileData.reduce((acc, row) => {
      const workshop = row[workshopColumn as string];
      if (workshop && workshop !== '') {
        const workshopKey = String(workshop).trim();
        if (!acc[workshopKey]) acc[workshopKey] = [];
        acc[workshopKey].push(row);
      }
      return acc;
    }, {} as Record<string, any[]>);
    
    // Update response counts
    const updatedResults = { ...analysisResults };
    Object.entries(workshopGroups).forEach(([workshop, rows]) => {
      if (updatedResults.by_workshop[workshop]) {
        updatedResults.by_workshop[workshop].response_count = rows.length;
        console.log(`✅ تم إصلاح عدد الاستجابات للورشة "${workshop}": ${rows.length}`);
      }
    });
    
    setAnalysisResults(updatedResults);
    console.log('✅ تم إصلاح جميع أعداد الاستجابات');
  }

  // Helper function to get performance level gradient
  function getPerformanceLevelGradient(level: string): string {
    switch (level) {
      case 'ممتاز': return 'bg-gradient-to-r from-green-400 to-green-600';
      case 'جيد جدا': return 'bg-gradient-to-r from-blue-400 to-blue-600';
      case 'جيد': return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
      case 'مقبول': return 'bg-gradient-to-r from-orange-400 to-orange-600';
      case 'ضعيف': return 'bg-gradient-to-r from-red-400 to-red-600';
      default: return 'bg-gradient-to-r from-gray-400 to-gray-600';
    }
  }

  // Helper function to get performance level class
  function getPerformanceLevelClass(level: string): string {
    switch (level) {
      case 'ممتاز': return 'performance-excellent';
      case 'جيد جدا': return 'performance-very-good';
      case 'جيد': return 'performance-good';
      case 'مقبول': return 'performance-acceptable';
      case 'ضعيف': return 'performance-weak';
      default: return 'bg-gray-500 text-white';
    }
  }

  // Helper function to clear and restart analysis
  function handleClearAndRestart() {
    console.log('🔧 مسح البيانات وإعادة التحليل...');
    
    // تحذير للمستخدم
    const confirmRestart = window.confirm(
      '⚠️ تحذير: هذا الإجراء سيمسح جميع النتائج الحالية ويعيد التحليل من البداية.\n\n' +
      'إذا كانت النتائج الحالية صحيحة، استخدم زر "إصلاح عدد الاستجابات" بدلاً من ذلك.\n\n' +
      'هل أنت متأكد من أنك تريد المتابعة؟'
    );
    
    if (!confirmRestart) {
      console.log('❌ تم إلغاء إعادة التحليل بواسطة المستخدم');
      return;
    }
    
    // Reset analysis results only
    setAnalysisResults(null as any);
    setError(null);
    
    // العودة لصفحة رفع الملف بدلاً من إعادة التحليل التلقائي
    console.log('📁 العودة لصفحة رفع الملف لبدء تحليل جديد...');
    setCurrentPage('file_upload');
    
    console.log('✅ تم مسح النتائج - يمكنك الآن رفع ملف جديد أو إعادة رفع نفس الملف');
  }
}